<template>
  <div class="card p-3 mb-3 alerta-item">
    <div class="mb-1">
      <span :class="badgeClasse" class="badge mr-1">{{ badgeTipo }}</span>
      <span v-if="alerta.criadoEm">{{ dataCriacao }}</span>
      <span v-if="tempoRestante" class="badge badge-secondary float-right contador-expiracao">{{ tempoRestante }}</span>
    </div>
    <h5 class="font-weight-bolder mb-0">{{ alerta.titulo }}</h5>
    <div v-if="alerta.mensagem">{{ alerta.mensagem }}</div>

    <div v-if="acaoSelecionada && !expirado" class="mt-2 mb-2">
      <select v-if="acaoSelecionada.exigeMotivo && acaoSelecionada.motivos.length" :id="`motivo-alerta-${alerta.id}`" v-model="motivo"
              :disabled="alerta.processando" aria-label="Motivo da recusa" class="form-control">
        <option value="">Selecione um motivo</option>
        <option v-for="item in acaoSelecionada.motivos" :key="item.valor" :value="item.valor">{{ item.label }}</option>
      </select>

      <textarea v-else-if="acaoSelecionada.exigeMotivo" :id="`motivo-alerta-${alerta.id}`"
                v-model="motivo" :disabled="alerta.processando" aria-label="Motivo da recusa"
                class="form-control" placeholder="Informe o motivo da recusa" rows="3"></textarea>

      <template v-else-if="tipoAlternativa === 'ADDITIONAL_TIME'">
        <label :for="`tempo-alerta-${alerta.id}`">Tempo adicional</label>
        <select :id="`tempo-alerta-${alerta.id}`" v-model="tempoAdicional" :disabled="alerta.processando" class="form-control mb-2">
          <option value="">Selecione o tempo adicional</option>
          <option v-for="tempo in temposPermitidos" :key="tempo" :value="tempo">{{ tempo }} minutos</option>
        </select>
        <label :for="`razao-alerta-${alerta.id}`">Motivo</label>
        <select :id="`razao-alerta-${alerta.id}`" v-model="razaoTempoAdicional" :disabled="alerta.processando" class="form-control">
          <option value="">Selecione um motivo</option>
          <option v-for="razao in razoesPermitidas" :key="razao" :value="razao">{{ razao }}</option>
        </select>
      </template>

      <template v-else-if="alternativaMonetaria">
        <label :for="`valor-alerta-${alerta.id}`">{{ labelAlternativaMonetaria }}</label>
        <money :id="`valor-alerta-${alerta.id}`" v-model="valorAlternativa" :disabled="alerta.processando"
               class="form-control"/>
        <small class="form-text text-muted">Valor máximo: {{ valorMaximoFormatado }}</small>
      </template>
    </div>

    <div v-if="expirado" class="alert alert-warning small my-2">O prazo para responder este alerta expirou.</div>

    <div :class="{'justify-content-between': alerta.pedidoId, 'justify-content-end': !alerta.pedidoId}" class="d-flex align-items-center border-top pt-2 mt-2">
      <button v-if="alerta.pedidoId" class="btn btn-outline-dark btn-sm" @click="$emit('ver-pedido', alerta.pedidoId)">Ver pedido</button>

      <div v-if="acaoSelecionada && !expirado" class="d-flex align-items-center alerta-acoes">
        <button :disabled="alerta.processando" class="btn btn-outline-dark btn-sm ml-2 px-3" @click="cancelar()">Voltar</button>
        <button :disabled="!formularioValido || alerta.processando" class="btn btn-secondary btn-sm ml-2 px-3" @click="confirmar()">
          <i v-if="alerta.processando" class="fa fa-spinner fa-spin mr-1"></i>Confirmar
        </button>
      </div>
      <div v-else-if="alerta.acoes.length && !expirado" class="d-flex align-items-center alerta-acoes">
        <button v-for="(acao, index) in alerta.acoes" :key="`${acao.resposta}-${index}`" :class="classeAcao(acao)"
                :disabled="alerta.processando || expirado || !acaoDisponivel(acao)" :title="mensagemAcaoIndisponivel(acao)"
                class="btn btn-sm ml-2 px-3" @click="selecionarAcao(acao)">
          <i v-if="alerta.processando" class="fa fa-spinner fa-spin mr-1"></i>{{ acao.label }}
        </button>
      </div>
      <button v-else :disabled="alerta.processando" class="btn btn-link btn-sm text-muted p-0" @click="$emit('ignorar', alerta)">
        Ignorar
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AlertaItem',
  props: { alerta: { type: Object, required: true } },
  data() {
    return {
      agora: this.$dayjs(),
      intervalo: null,
      acaoSelecionada: null,
      motivo: '',
      tempoAdicional: '',
      razaoTempoAdicional: '',
      valorAlternativa: 0
    };
  },
  computed: {
    dataCriacao() {
      return this.$dayjs(this.alerta.criadoEm).format('DD/MM/YYYY - HH:mm');
    },
    badgeClasse() {
      return this.alerta.tipo === 2 ? 'badge-warning' : 'badge-danger';
    },
    badgeTipo() {
      return this.alerta.tipo === 2 ? 'Solicitação' : 'Ocorrência';
    },
    expirado() {
      return this.alerta.expirado || (!!this.alerta.expiraEm && !this.$dayjs(this.alerta.expiraEm).isAfter(this.agora));
    },
    tempoRestante() {
      if (!this.alerta.expiraEm || this.expirado) return '';
      const segundos = this.$dayjs(this.alerta.expiraEm).diff(this.agora, 'second');
      const horas = Math.floor(segundos / 3600);
      const minutos = Math.floor((segundos % 3600) / 60);
      const resto = segundos % 60;
      return horas > 0
        ? `Expira em ${horas}h ${String(minutos).padStart(2, '0')}min`
        : `Expira em ${String(minutos).padStart(2, '0')}:${String(resto).padStart(2, '0')}`;
    },
    tipoAlternativa() {
      return this.acaoSelecionada && this.acaoSelecionada.metadata
        ? this.acaoSelecionada.metadata.type
        : null;
    },
    alternativaMonetaria() {
      return ['REFUND', 'BENEFIT'].includes(this.tipoAlternativa);
    },
    temposPermitidos() {
      return this.tipoAlternativa === 'ADDITIONAL_TIME'
        ? this.acaoSelecionada.metadata.allowedsAdditionalTimeInMinutes
        : [];
    },
    razoesPermitidas() {
      return this.tipoAlternativa === 'ADDITIONAL_TIME'
        ? this.acaoSelecionada.metadata.allowedsAdditionalTimeReasons
        : [];
    },
    valorAlternativaCentavos() {
      return Math.round(Number(this.valorAlternativa) * 100);
    },
    valorMaximoCentavos() {
      if (!this.alternativaMonetaria) return 0;
      return Number(this.acaoSelecionada.metadata.maxAmount.value);
    },
    valorMaximoFormatado() {
      if (!this.alternativaMonetaria) return '';
      const maxAmount = this.acaoSelecionada.metadata.maxAmount;
      try {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: maxAmount.currency })
          .format(this.valorMaximoCentavos / 100);
      } catch (e) {
        return `${(this.valorMaximoCentavos / 100).toFixed(2)} ${maxAmount.currency}`;
      }
    },
    labelAlternativaMonetaria() {
      return this.tipoAlternativa === 'BENEFIT'
        ? 'Oferecer crédito/benefício ao cliente'
        : 'Oferecer reembolso ao cliente';
    },
    formularioValido() {
      if (!this.acaoSelecionada || this.expirado) return false;
      if (this.acaoSelecionada.exigeMotivo) {
        return this.acaoSelecionada.motivos.length
          ? this.acaoSelecionada.motivos.some(item => item.valor === this.motivo)
          : !!this.motivo.trim();
      }
      if (this.tipoAlternativa === 'ADDITIONAL_TIME') {
        return this.temposPermitidos.includes(this.tempoAdicional)
          && this.razoesPermitidas.includes(this.razaoTempoAdicional);
      }
      if (this.alternativaMonetaria) {
        return Number.isSafeInteger(this.valorAlternativaCentavos)
          && this.valorAlternativaCentavos > 0
          && this.valorAlternativaCentavos <= this.valorMaximoCentavos;
      }
      return true;
    }
  },
  methods: {
    classeAcao(acao) {
      return Number(acao.resposta) === 2 ? 'btn-secondary' : 'btn-success';
    },
    selecionarAcao(acao) {
      if (!this.acaoDisponivel(acao)) return;
      if (acao.exigeMotivo || Number(acao.resposta) === 3) {
        this.acaoSelecionada = acao;
        this.limparFormulario();
      } else {
        this.$emit('responder', { alerta: this.alerta, acao });
      }
    },
    confirmar() {
      if (!this.formularioValido) return;
      const dados = { alerta: this.alerta, acao: this.acaoSelecionada };
      if (this.acaoSelecionada.exigeMotivo) dados.motivo = this.motivo.trim();
      if (this.tipoAlternativa === 'ADDITIONAL_TIME') {
        dados.alternative = {
          type: 'ADDITIONAL_TIME',
          metadata: { additionalTimeInMinutes: this.tempoAdicional, reason: this.razaoTempoAdicional }
        };
      } else if (this.alternativaMonetaria) {
        dados.alternative = {
          type: this.tipoAlternativa,
          metadata: {
            amount: {
              value: String(this.valorAlternativaCentavos),
              currency: this.acaoSelecionada.metadata.maxAmount.currency
            }
          }
        };
      }
      this.$emit('responder', dados);
    },
    cancelar() {
      this.acaoSelecionada = null;
      this.limparFormulario();
    },
    limparFormulario() {
      this.motivo = '';
      this.tempoAdicional = '';
      this.razaoTempoAdicional = '';
      this.valorAlternativa = 0;
    },
    acaoDisponivel(acao) {
      const resposta = Number(acao.resposta);
      if (![1, 2, 3].includes(resposta)) return false;
      if (resposta !== 3) return true;
      if (acao.nome !== 'ALTERNATIVE') return false;
      const metadata = acao.metadata;
      if (!metadata || !['REFUND', 'BENEFIT', 'ADDITIONAL_TIME'].includes(metadata.type)) return false;
      if (metadata.type === 'ADDITIONAL_TIME') {
        return Array.isArray(metadata.allowedsAdditionalTimeInMinutes)
          && metadata.allowedsAdditionalTimeInMinutes.length > 0
          && metadata.allowedsAdditionalTimeInMinutes.every(item => Number.isFinite(item))
          && Array.isArray(metadata.allowedsAdditionalTimeReasons)
          && metadata.allowedsAdditionalTimeReasons.length > 0;
      }
      const maxAmount = metadata.maxAmount;
      return !!maxAmount
        && typeof maxAmount.value === 'string'
        && /^[1-9]\d*$/.test(maxAmount.value)
        && Number.isSafeInteger(Number(maxAmount.value))
        && typeof maxAmount.currency === 'string'
        && !!maxAmount.currency;
    },
    mensagemAcaoIndisponivel(acao) {
      return this.acaoDisponivel(acao) ? '' : 'Esta alternativa não está disponível.';
    }
  },
  mounted() {
    this.intervalo = setInterval(() => {
      this.agora = this.$dayjs();
    }, 1000);
  },
  beforeDestroy() {
    clearInterval(this.intervalo);
  }
}
</script>

<style scoped>
.alerta-acoes { margin-left: -8px; }
.contador-expiracao {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}
</style>
